<template>
    <div    class="h-screen">

        <slide-over-panel     v-model:show="showLocation"
        >
            <template   #title>
                Position Details
            </template>
            <template   #content>
                <div    v-if="positionHighlight"
                        class="flex flex-col">
                    <span>Time: {{ positionHighlight.time }}</span>
                    <span>Latitude: {{ positionHighlight.latitude }}</span>
                    <span>Longitude: {{ positionHighlight.longitude }}</span>
                </div>
            </template>
        </slide-over-panel>

        <div    class="flex flex-row items-center">
            <span   class="font-semibold text-lg">
                GPS History for {{ physicalSerial }}
            </span>

            <div    >
                <span   v-if="loadingPositionHistory"
                        :class="iconLoading"
                ></span>

                <!-- Refresh Icon -->
                <span   v-else
                        @click="loadData"
                        :class="iconRefresh"
                ></span>
            </div>

        </div>

        <!-- Start & End -->
        <div    class="flex flex-col w-full md:w-1/3 lg:1/5 xl:1/12 my-2 mb-4">
            <!-- Start Date Time Picker -->
            <div    class="flex inline-center my-1">
                <span   class="w-1/4">Start</span>
                <date-time-picker   v-model="start"
                                    id="startDateTimePicker"
                                    :timezone="timezone"
                ></date-time-picker>
            </div>

            <!-- End Date Time Picker -->
            <div    class="flex inline-center my-1">
                <span   class="w-1/4">End</span>
                <date-time-picker   v-model="end"
                                    id="endDateTimePicker"
                                    :timezone="timezone"
                ></date-time-picker>
            </div>
        </div>

        <div    class="flex flex-col">
            <span>Last Reported At. {{ getLatestReadingTime() }}</span>
            <span>Latitude: {{ getLatestLatitude() }}</span>
            <span>Longitude: {{ getLatestLongitude() }}</span>
        </div>

        <div    v-show="positionHistory.length > 0"
                class="google-map h-1/2 mt-4" ref="googleMap"></div>

        <div    v-show="positionHistory.length === 0 && loadingPositionHistory"
                class="mt-8 flex justify-around">
            <span>Loading Positional Data</span>
        </div>

        <div    v-show="positionHistory.length === 0 && !loadingPositionHistory"
                class="mt-8 flex justify-around"
        >
            <span>No Position Data Found.</span>
        </div>
    </div>
</template>

<script>
import axios from "axios"
import _ from "lodash"

import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {iconNamesMixin} from '@/mixins/IconNamesMixin';
import {UrlParamsMixin} from "@/mixins/UrlParamsMixin";
import { Loader } from '@googlemaps/js-api-loader';
import SlideOverPanel from "@/components/elements/SlideOverPanel";
import DateTimePicker from "@/components/Reports/DateTimePicker";

export default {
    name: "GpsTracking",
    mixins: [SonicuCoreMixin, iconNamesMixin, UrlParamsMixin],
    components: {DateTimePicker, SlideOverPanel},
    props: {
        latitudeId: Number,
        longitudeId: Number,
        physicalSerial: String,
        startDateInput: String,
        endDateInput: String,
        googleApiKey: String,
        timezone: String,

    },
    data() {
        return {
            loadingPositionHistory: false,
            positionHistory: [],
            lastCheckin: null,
            start: null,
            end: null,
            positionHighlight: null,
            showLocation: false,
            lastRequestNumber: -1,

            // Google Maps Data
            map: null,
            coordinates: [],
            markers: [],
            line: null,
        }
    },
    created() {
        this.start =  new Date(_.clone(this.startDateInput))
        this.end = new Date(_.clone(this.endDateInput))
    },
    mounted() {
        this.initializeMap()
        this.loadData()
    },
    methods: {
        loadData() {
            this.loadingPositionHistory = true
            this.lastRequestNumber++
            axios.get(this.company.buildCompanyApiInternalRoute(`gps-tracking`), {
                params: {
                    latitudeId: this.latitudeId,
                    longitudeId: this.longitudeId,
                    start: this.start,
                    end: this.end,
                    requestNumber: this.lastRequestNumber
                }
            }).then(response => {
                let data = response.data
                if (`${data.requestNumber}` !== `${this.lastRequestNumber}`) {
                    return
                }

                this.positionHistory = data.gpsPoints
                this.lastCheckin = data.lastCheckin
                this.loadDataOntoMap();
            }).catch(error => {
                console.log(`Error: ${error}`)
            }).then(() => {
                this.loadingPositionHistory = false
            })
        },
        initializeMap(){
            const loader = new Loader({
                apiKey: this.googleApiKey,
                version: 'weekly',
                libraries: ['places']
            });

            loader.load().then(() => {
                const mapContainer = this.$refs.googleMap
                this.map = new google.maps.Map(mapContainer, {
                    zoom: 8,
                });

                // Now we'll tell it to load the data initially
                this.loadDataOntoMap();
            });
        },
        loadDataOntoMap(){
            this.clearMarkers();
            this.coordinates = [];

            // Create markers for each point
            this.positionHistory.forEach((value, index) => {
                let coord = {lat: value.latitude, lng: value.longitude}
                let marker = new google.maps.Marker({
                    position: coord,
                    map: this.map,
                    icon: index === (this.positionHistory.length - 1) ? 'http://maps.google.com/mapfiles/ms/icons/green-dot.png' : 'http://maps.google.com/mapfiles/ms/icons/red-dot.png'
                })
                marker.addListener("click", () => {
                    this.positionHighlight = value
                    this.showLocation = true
                })
                this.markers.push(marker)
                this.coordinates.push(coord)
            });
            this.map.setCenter(this.coordinates[this.coordinates.length - 1]);

            this.buildLine();
        },
        clearMarkers(){
            this.markers.forEach((marker) => marker.setMap(null))
            this.markers = [];
        },
        buildLine(){
            if(this.line) {
                this.line.setMap(null);
            }
            this.line = new google.maps.Polyline({
                path: this.coordinates,
                geodesic: true,
                strokeColor: '#FF0000',
                strokeOpacity: 1.0,
                strokeWeight: 2,
            });

            this.line.setMap(this.map);
        },
        getLatestLongitude() {
            if (this.positionHistory.length === 0) {
                return 'N/A'
            }

            return this.positionHistory[this.positionHistory.length - 1]['longitude']
        },
        getLatestLatitude() {
            if (this.positionHistory.length === 0) {
                return 'N/A'
            }

            return this.positionHistory[this.positionHistory.length - 1]['latitude']
        },
        getLatestReadingTime() {
            if (this.positionHistory.length === 0) {
                return _.isNil(this.lastCheckin) ? 'N/A' : this.lastCheckin;
            }

            return this.positionHistory[this.positionHistory.length - 1]['time']
        },
    },
    computed: {
        
    },
    watch: {
        showLocation() {
            if (!this.showLocation) {
                this.positionHighlight = null
            }
        },
        start(val, old) {
            this.urlParamsStore('start', this.start.toISOString())
            if (val !== old) {
                this.loadData()
            }
        },
        end(val, old) {
            this.urlParamsStore('end', this.end.toISOString())

            if (val !== old) {
                this.loadData()
            }
        },
    },
}
</script>

<style scoped>

</style>